import { useTranslation } from 'react-i18next'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip'
import { useLiveStatus } from '../contexts/LiveEventsContext'

/**
 * LiveStatusIndicator is a small dot in the header showing whether the live
 * entity-notifications WebSocket is connected. Optional per the spec — it
 * doesn't gate any functionality (polling keeps working regardless) but
 * helps manual verification and e2e assertions have something concrete to
 * check. Renders nothing while permanently unavailable and never connected
 * (realtime.enabled=false, or the very first connect got a 4403) — a
 * feature that's off shouldn't show a persistent "offline" warning for
 * something the user can't do anything about.
 */
export function LiveStatusIndicator() {
    const { t } = useTranslation()
    const { isLive, isPermanentlyUnavailable } = useLiveStatus()

    if (isPermanentlyUnavailable && !isLive) {
        return null
    }

    const label = isLive
        ? t('live_status.live')
        : t('live_status.connecting')

    return (
        <TooltipProvider>
            <Tooltip>
                <TooltipTrigger asChild>
                    <span
                        className="inline-flex items-center justify-center p-2"
                        data-testid="live-status-indicator"
                        data-live={isLive}
                        aria-label={label}
                    >
                        <span
                            className={`h-2 w-2 rounded-full transition-colors ${isLive ? 'bg-emerald-500' : 'bg-muted-foreground/40'
                                }`}
                        />
                    </span>
                </TooltipTrigger>
                <TooltipContent>
                    <p>{label}</p>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    )
}
